<template>
  <section aria-labelledby="store-locator-list-heading" class="flex flex-col gap-4">
    <div class="border-b border-neutral-200 pb-4">
      <StoreLocatorSearch
        :is-loading="isLoading"
        @search="emit('search', $event)"
        @geolocate="emit('geolocate', $event)"
      />
    </div>

    <div class="flex items-center justify-between">
      <p
        id="store-locator-list-heading"
        class="text-lg font-normal leading-7 text-neutral-600"
        aria-live="polite"
        aria-atomic="true"
      >
        {{ $t('storeLocator.storesCount', { count: total }) }}
      </p>

      <div
        class="flex items-start gap-2 overflow-hidden rounded-2xl border-b border-neutral-200 bg-primary-50 md:hidden"
        role="tablist"
        :aria-label="$t('storeLocator.viewToggle')"
      >
        <button
          role="tab"
          :aria-selected="activeTab === 'list'"
          :aria-label="$t('storeLocator.listView')"
          :class="[
            'flex flex-1 flex-col items-center pb-1 transition-all',
            activeTab === 'list' ? 'border-b-4 border-primary-700' : '',
          ]"
          @click="emit('tab-change', 'list')"
        >
          <div class="flex w-full flex-col items-center justify-center rounded-md px-4 py-2">
            <component
              :is="IconList"
              :class="['size-6', activeTab === 'list' ? 'text-primary-700' : 'text-neutral-500']"
              aria-hidden="true"
            />
          </div>
        </button>
        <button
          role="tab"
          :aria-selected="activeTab === 'map'"
          :aria-label="$t('storeLocator.mapView')"
          :class="[
            'flex flex-1 flex-col items-center pb-1 transition-all',
            activeTab === 'map' ? 'border-b-4 border-primary-700' : '',
          ]"
          @click="emit('tab-change', 'map')"
        >
          <div class="flex w-full flex-col items-center justify-center rounded-md px-4 py-2">
            <component
              :is="IconMap"
              :class="['size-6', activeTab === 'map' ? 'text-primary-700' : 'text-neutral-500']"
              aria-hidden="true"
            />
          </div>
        </button>
      </div>
    </div>

    <ul
      :class="[
        'flex flex-col gap-2 overflow-y-auto overflow-x-hidden md:h-[529px]',
        activeTab === 'map' ? 'hidden md:flex' : '',
      ]"
    >
      <li v-for="store in stores" :key="store.id">
        <StoreLocatorCard :store="store" :active-store-id="activeStoreId" @select="emit('select', $event)" />
      </li>
    </ul>

    <StoreLocatorPagination
      v-show="activeTab !== 'map'"
      :current-page="currentPage"
      :total-pages="totalPages"
      @page-change="emit('page-change', $event)"
    />
  </section>
</template>

<script setup lang="ts">
import { IconList, IconMap } from '#components';

import type { StoreLocatorListEmits, StoreLocatorListProps } from '~/components/StoreLocator/types';

withDefaults(defineProps<StoreLocatorListProps>(), {
  activeStoreId: undefined,
  activeTab: 'list',
  isLoading: false,
});
const emit = defineEmits<StoreLocatorListEmits>();
</script>
